/**
 * Element Effect Configurations (L4-VIS-004)
 *
 * Visual configs for each element type used in dynamic abilities.
 * Maps elements to colors, particles, blend modes, and projectile styles.
 *
 * NOTE: baseParticleCount is multiplied by importance (minor: 1x, moderate: 2x, major: 4x, ultimate: 8x)
 */

export type ElementType = 'physical' | 'fire' | 'ice' | 'lightning' | 'poison' | 'dark' | 'holy' | 'arcane' | 'nature';
export type VisualImportance = 'minor' | 'moderate' | 'major' | 'ultimate';

export interface ElementEffectConfig {
  // Colors
  primaryColor: number;      // Main hex color (e.g., 0xFF4400 for fire)
  secondaryColor: number;    // Accent color
  glowColor: number;         // Glow/aura color

  // Particles (L4-VIS-004: Scaled by visual importance)
  particleType: 'flame' | 'snowflake' | 'spark' | 'bubble' | 'shadow' | 'holy_ray' | 'rune' | 'leaf' | 'dust';
  baseParticleCount: number; // Base particles (scaled by importance: 1x/2x/4x/8x)
  particleLifetime: number;  // MS (200-1000)
  particleSpeed: number;     // Pixels per second (50-200)

  // Rendering
  blendMode: string;         // PIXI v8 blend mode (e.g., 'normal', 'add', 'screen')
  glowIntensity: number;     // 0-1
  trailEnabled: boolean;

  // Projectile style
  projectileShape: 'ball' | 'shard' | 'bolt' | 'orb' | 'arrow' | 'wave';
  projectileScale: number;   // 0.5-2.0
  projectileRotates: boolean;

  // Impact
  impactScale: number;       // 0.5-2.0
  impactDuration: number;    // MS
  screenShake: boolean;
}

export const ELEMENT_EFFECT_CONFIGS: Record<ElementType, ElementEffectConfig> = {
  physical: {
    primaryColor: 0xCCCCCC,
    secondaryColor: 0x888888,
    glowColor: 0xFFFFFF,
    particleType: 'dust',
    baseParticleCount: 15,
    particleLifetime: 300,
    particleSpeed: 80,
    blendMode: 'normal',
    glowIntensity: 0.2,
    trailEnabled: false,
    projectileShape: 'arrow',
    projectileScale: 1.0,
    projectileRotates: true,
    impactScale: 1.0,
    impactDuration: 200,
    screenShake: true
  },

  fire: {
    primaryColor: 0xFF4400,
    secondaryColor: 0xFFAA00,
    glowColor: 0xFF6600,
    particleType: 'flame',
    baseParticleCount: 30,
    particleLifetime: 500,
    particleSpeed: 120,
    blendMode: 'add',
    glowIntensity: 0.8,
    trailEnabled: true,
    projectileShape: 'ball',
    projectileScale: 1.2,
    projectileRotates: false,
    impactScale: 1.5,
    impactDuration: 400,
    screenShake: true
  },

  ice: {
    primaryColor: 0x00CCFF,
    secondaryColor: 0xAAFFFF,
    glowColor: 0x66DDFF,
    particleType: 'snowflake',
    baseParticleCount: 25,
    particleLifetime: 600,
    particleSpeed: 60,
    blendMode: 'screen',
    glowIntensity: 0.6,
    trailEnabled: true,
    projectileShape: 'shard',
    projectileScale: 1.0,
    projectileRotates: true,
    impactScale: 1.3,
    impactDuration: 500,
    screenShake: false
  },

  lightning: {
    primaryColor: 0xFFFF00,
    secondaryColor: 0xAAAAFF,
    glowColor: 0xFFFFAA,
    particleType: 'spark',
    baseParticleCount: 40,
    particleLifetime: 200,
    particleSpeed: 200,
    blendMode: 'add',
    glowIntensity: 1.0,
    trailEnabled: true,
    projectileShape: 'bolt',
    projectileScale: 0.8,
    projectileRotates: false,
    impactScale: 1.8,
    impactDuration: 150,
    screenShake: true
  },

  poison: {
    primaryColor: 0x00FF00,
    secondaryColor: 0x88FF88,
    glowColor: 0x44FF44,
    particleType: 'bubble',
    baseParticleCount: 20,
    particleLifetime: 800,
    particleSpeed: 40,
    blendMode: 'soft-light',
    glowIntensity: 0.5,
    trailEnabled: true,
    projectileShape: 'orb',
    projectileScale: 1.1,
    projectileRotates: false,
    impactScale: 1.2,
    impactDuration: 600,
    screenShake: false
  },

  dark: {
    primaryColor: 0x440066,
    secondaryColor: 0x220033,
    glowColor: 0x8800AA,
    particleType: 'shadow',
    baseParticleCount: 25,
    particleLifetime: 700,
    particleSpeed: 70,
    blendMode: 'multiply',
    glowIntensity: 0.7,
    trailEnabled: true,
    projectileShape: 'orb',
    projectileScale: 1.3,
    projectileRotates: false,
    impactScale: 1.4,
    impactDuration: 500,
    screenShake: true
  },

  holy: {
    primaryColor: 0xFFDD00,
    secondaryColor: 0xFFFFFF,
    glowColor: 0xFFFF88,
    particleType: 'holy_ray',
    baseParticleCount: 20,
    particleLifetime: 600,
    particleSpeed: 100,
    blendMode: 'add',
    glowIntensity: 0.9,
    trailEnabled: true,
    projectileShape: 'wave',
    projectileScale: 1.2,
    projectileRotates: false,
    impactScale: 1.6,
    impactDuration: 400,
    screenShake: false
  },

  arcane: {
    primaryColor: 0x8800FF,
    secondaryColor: 0xDD88FF,
    glowColor: 0xAA44FF,
    particleType: 'rune',
    baseParticleCount: 30,
    particleLifetime: 500,
    particleSpeed: 90,
    blendMode: 'add',
    glowIntensity: 0.8,
    trailEnabled: true,
    projectileShape: 'orb',
    projectileScale: 1.0,
    projectileRotates: true,
    impactScale: 1.4,
    impactDuration: 350,
    screenShake: false
  },

  nature: {
    primaryColor: 0x228B22,
    secondaryColor: 0x8B4513,
    glowColor: 0x44AA44,
    particleType: 'leaf',
    baseParticleCount: 25,
    particleLifetime: 900,
    particleSpeed: 50,
    blendMode: 'soft-light',
    glowIntensity: 0.4,
    trailEnabled: false,
    projectileShape: 'wave',
    projectileScale: 1.1,
    projectileRotates: false,
    impactScale: 1.2,
    impactDuration: 500,
    screenShake: false
  }
};

/**
 * Get element config with fallback to physical
 */
export function getElementConfig(element: string): ElementEffectConfig {
  return ELEMENT_EFFECT_CONFIGS[element as ElementType] || ELEMENT_EFFECT_CONFIGS.physical;
}

/**
 * Get color for damage number based on element
 */
export function getElementDamageColor(element: string): number {
  const config = getElementConfig(element);
  return config.primaryColor;
}

/**
 * Get particle count multiplier for visual importance (L4-VIS-004)
 * - minor: 1x base particles
 * - moderate: 2x base particles
 * - major: 4x base particles
 * - ultimate: 8x base particles
 */
export function getParticleMultiplier(importance: VisualImportance): number {
  const multipliers: Record<VisualImportance, number> = {
    minor: 1.0,
    moderate: 2.0,
    major: 4.0,
    ultimate: 8.0
  };
  return multipliers[importance];
}

/**
 * Calculate scaled particle count based on element and importance (L4-VIS-004)
 *
 * @param element - Element type
 * @param importance - Visual importance level (defaults to 'minor')
 * @returns Scaled particle count
 */
export function getScaledParticleCount(
  element: string,
  importance: VisualImportance = 'minor'
): number {
  const config = getElementConfig(element);
  const multiplier = getParticleMultiplier(importance);
  return Math.round(config.baseParticleCount * multiplier);
}

/**
 * Get scaled effect config with importance-adjusted particle count (L4-VIS-004)
 *
 * @param element - Element type
 * @param importance - Visual importance level (defaults to 'minor')
 * @returns Element config with scaled particle count
 */
export function getScaledElementConfig(
  element: string,
  importance: VisualImportance = 'minor'
): ElementEffectConfig & { particleCount: number } {
  const config = getElementConfig(element);
  return {
    ...config,
    particleCount: getScaledParticleCount(element, importance)
  };
}

/**
 * Get visual size multiplier based on importance (L4-VIS-005)
 * Used to scale effect sprites, AOE radii, and overall visual impact
 *
 * @param importance - Visual importance level
 * @returns Size multiplier (0.8x to 2.5x)
 */
export function getSizeMultiplier(importance: VisualImportance): number {
  const multipliers: Record<VisualImportance, number> = {
    minor: 0.8,
    moderate: 1.2,
    major: 1.8,
    ultimate: 2.5
  };
  return multipliers[importance];
}
